import React, { useState, useEffect, useCallback } from 'react';
import { API, getToken } from './adminApi';
import GranularInterconnectingEmails from './GranularInterconnectingEmails';
import DirectEmailTab from './DirectEmailTab';
import InboxPanel from './InboxPanel';
import AutoEmailsPanel from './AutoEmailsPanel';
import EmailHistoryPanel from './EmailHistoryPanel';
import RichBodyEditor from './RichBodyEditor';
import TemplateLibrary from './TemplateLibrary';

const fmtDate = (d) => d
  ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const EMPTY_DRAFT = {
  name: '',
  communityId: '',
  subject: '',
  htmlBody: '',
  newMemberDays: '',
  profileCompleteness: 'either',
};

const EmailManagerTab = () => {
  const [section, setSection] = useState('campaigns'); // 'campaigns' | 'suggestions' | 'inbox'
  const [view, setView] = useState('compose'); // 'compose' | 'campaigns' | 'log' — sub-nav within the Campaigns section
  const [error, setError] = useState('');

  const [communities, setCommunities] = useState([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSenderId, setEditingSenderId] = useState(null);
  const [senderPrefixDraft, setSenderPrefixDraft] = useState('');
  const [savingSender, setSavingSender] = useState(false);

  const [campaigns, setCampaigns] = useState(null);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [sendingCampaignId, setSendingCampaignId] = useState(null);
  const [sendResult, setSendResult] = useState(null); // { campaignId, ...summary }

  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);

  const [viewingHistoryUserId, setViewingHistoryUserId] = useState(null);

  const authedFetch = useCallback(async (path, options = {}) => {
    const token = await getToken();
    const res = await fetch(`${API}/admin${path}`, {
      ...options,
      headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Request failed');
    return json;
  }, []);

  useEffect(() => {
    authedFetch('/email/communities').then(d => setCommunities(d.communities)).catch(e => setError(e.message));
  }, [authedFetch]);

  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const data = await authedFetch('/email/campaigns');
      setCampaigns(data.campaigns);
    } catch (e) {
      setError(e.message);
    } finally {
      setCampaignsLoading(false);
    }
  }, [authedFetch]);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const data = await authedFetch('/email/log?limit=200');
      setLogs(data.logs);
    } catch (e) {
      setError(e.message);
    } finally {
      setLogsLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    if (view === 'campaigns' && !campaigns) fetchCampaigns();
    if (view === 'log' && !logs) fetchLogs();
  }, [view, campaigns, logs, fetchCampaigns, fetchLogs]);

  const runPreview = async (filters, campaignId = null) => {
    setPreviewLoading(true);
    setError('');
    try {
      const data = await authedFetch('/email/preview-recipients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...filters, campaignId }),
      });
      setPreview(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const startEditingSender = (community) => {
    setEditingSenderId(community._id);
    setSenderPrefixDraft(community.emailPrefix);
  };

  const handleSaveSenderPrefix = async (communityId) => {
    setSavingSender(true);
    setError('');
    try {
      const data = await authedFetch(`/email/communities/${communityId}/sender`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailPrefix: senderPrefixDraft }),
      });
      setCommunities(prev => prev.map(c => (c._id === communityId ? { ...c, emailPrefix: data.emailPrefix, senderAddress: data.senderAddress } : c)));
      setEditingSenderId(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingSender(false);
    }
  };

  const handleDraftPreview = () => {
    if (!draft.communityId) return setError('Choose a community first');
    runPreview({
      communityId: draft.communityId,
      newMemberDays: draft.newMemberDays ? parseInt(draft.newMemberDays) : null,
      profileCompleteness: draft.profileCompleteness,
    });
  };

  const handleSaveCampaign = async () => {
    if (!draft.name || !draft.subject || !draft.htmlBody || !draft.communityId) {
      setError('Name, community, subject and body are all required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await authedFetch('/email/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...draft,
          newMemberDays: draft.newMemberDays ? parseInt(draft.newMemberDays) : null,
        }),
      });
      setDraft(EMPTY_DRAFT);
      setPreview(null);
      setCampaigns(null); // force refetch next time campaigns tab is opened
      setView('campaigns');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendCampaign = async (campaign) => {
    const confirmed = window.confirm(
      `Send "${campaign.name}" now? This will actually email every matching recipient who hasn't already received it.`
    );
    if (!confirmed) return;

    setSendingCampaignId(campaign._id);
    setError('');
    try {
      const data = await authedFetch(`/email/campaigns/${campaign._id}/send`, { method: 'POST' });
      setSendResult({ campaignId: campaign._id, ...data });
      setCampaigns(null);
      fetchCampaigns();
    } catch (e) {
      setError(e.message);
    } finally {
      setSendingCampaignId(null);
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"? Its send log is kept.`)) return;
    try {
      await authedFetch(`/email/campaigns/${campaign._id}`, { method: 'DELETE' });
      setCampaigns(prev => prev.filter(c => c._id !== campaign._id));
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="as-signups">
      {error && <div className="as-error">{error}</div>}

      <div className="as-activity-toolbar">
        <button className={`as-filter-tab ${section === 'suggestions' ? 'as-filter-tab--active' : ''}`} onClick={() => setSection('suggestions')}>🤝 Interconnecting</button>
        <button className={`as-filter-tab ${section === 'campaigns' ? 'as-filter-tab--active' : ''}`} onClick={() => setSection('campaigns')}>📣 Campaigns</button>
        <button className={`as-filter-tab ${section === 'inbox' ? 'as-filter-tab--active' : ''}`} onClick={() => setSection('inbox')}>📥 Inbox</button>
        <button className={`as-filter-tab ${section === 'direct' ? 'as-filter-tab--active' : ''}`} onClick={() => setSection('direct')}>✉️ Create Message</button>
        <button className={`as-filter-tab ${section === 'auto' ? 'as-filter-tab--active' : ''}`} onClick={() => setSection('auto')}>⚡ Auto emails</button>
      </div>

      {section === 'suggestions' && <GranularInterconnectingEmails />}
      {section === 'inbox' && <InboxPanel authedFetch={authedFetch} />}
      {section === 'direct' && <DirectEmailTab />}
      {section === 'auto' && <AutoEmailsPanel authedFetch={authedFetch} communities={communities} />}

      {section === 'campaigns' && (
        <>
      <div className="as-activity-toolbar">
        <button className={`as-filter-tab ${view === 'compose' ? 'as-filter-tab--active' : ''}`} onClick={() => setView('compose')}>✏️ New Campaign</button>
        <button className={`as-filter-tab ${view === 'campaigns' ? 'as-filter-tab--active' : ''}`} onClick={() => setView('campaigns')}>📋 Campaigns</button>
        <button className={`as-filter-tab ${view === 'log' ? 'as-filter-tab--active' : ''}`} onClick={() => setView('log')}>🧾 Send Log</button>
      </div>

      {view === 'compose' && (
        <div className="as-email-compose">
          <div className="as-email-field">
            <label>Community</label>
            <select value={draft.communityId} onChange={e => { setDraft(d => ({ ...d, communityId: e.target.value })); setPreview(null); }}>
              <option value="">Choose a community…</option>
              {communities.map(c => (
                <option key={c._id} value={c._id}>{c.title} — sends from {c.senderAddress}</option>
              ))}
            </select>
          </div>

          {draft.communityId && (() => {
            const selected = communities.find(c => c._id === draft.communityId);
            if (!selected) return null;
            const isEditing = editingSenderId === selected._id;
            return (
              <div className="as-email-sender-row">
                {!isEditing ? (
                  <>
                    <span>Sends from <strong>{selected.senderAddress}</strong></span>
                    <button type="button" className="as-filter-tab" onClick={() => startEditingSender(selected)}>Change</button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      value={senderPrefixDraft}
                      onChange={e => setSenderPrefixDraft(e.target.value)}
                      placeholder={selected.title.toLowerCase().replace(/[^a-z0-9]/g, '')}
                    />
                    <span>@theinterconnector.com</span>
                    <button type="button" className="as-btn as-btn--approve" onClick={() => handleSaveSenderPrefix(selected._id)} disabled={savingSender}>
                      {savingSender ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" className="as-filter-tab" onClick={() => setEditingSenderId(null)}>Cancel</button>
                  </>
                )}
              </div>
            );
          })()}

          <div className="as-email-field">
            <label>Campaign name (internal, not sent)</label>
            <input type="text" value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="e.g. UK Bikers Welcome" />
          </div>

          <div className="as-email-field-row">
            <div className="as-email-field">
              <label>Only members who joined in the last…</label>
              <input type="number" min="0" value={draft.newMemberDays} onChange={e => { setDraft(d => ({ ...d, newMemberDays: e.target.value })); setPreview(null); }} placeholder="days (blank = no limit)" />
            </div>
            <div className="as-email-field">
              <label>Profile completeness</label>
              <select value={draft.profileCompleteness} onChange={e => { setDraft(d => ({ ...d, profileCompleteness: e.target.value })); setPreview(null); }}>
                <option value="either">Either</option>
                <option value="complete">Complete only</option>
                <option value="incomplete">Incomplete only</option>
              </select>
            </div>
          </div>

          <TemplateLibrary
            authedFetch={authedFetch}
            basePath="/email-templates"
            subject={draft.subject}
            body={draft.htmlBody}
            onLoad={({ subject, body }) => setDraft(d => ({ ...d, subject, htmlBody: body }))}
          />

          <div className="as-email-field">
            <label>Subject</label>
            <input type="text" value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} placeholder="Welcome to {{communityName}}!" />
          </div>

          <div className="as-email-field">
            <label>Body — {'{{firstName}}'} and {'{{communityName}}'} are replaced per recipient</label>
            <RichBodyEditor
              value={draft.htmlBody}
              onChange={(html) => setDraft(d => ({ ...d, htmlBody: html }))}
              placeholder="Hi {{firstName}}, welcome to {{communityName}}..."
            />
          </div>

          <div className="as-email-actions">
            <button className="as-filter-tab" onClick={handleDraftPreview} disabled={previewLoading}>
              {previewLoading ? 'Checking…' : '👀 Preview Recipients'}
            </button>
            <button className="as-btn as-btn--approve" onClick={handleSaveCampaign} disabled={saving}>
              {saving ? 'Saving…' : 'Save Campaign'}
            </button>
          </div>

          {preview && (
            <div className="as-email-preview">
              <div className="as-email-preview-count">
                <strong>{preview.count}</strong> recipient{preview.count === 1 ? '' : 's'} would receive this
                {preview.excludedAsAlreadySent > 0 && <span className="as-email-preview-excluded"> ({preview.excludedAsAlreadySent} already emailed, excluded)</span>}
              </div>
              <div className="as-email-preview-from">From: {preview.community.senderAddress}</div>
              {preview.sample.length > 0 && (
                <ul className="as-email-preview-sample">
                  {preview.sample.map(r => (
                    <li key={r.userId}>
                      {r.firstName || '(no name)'} — {r.email}{' '}
                      <button type="button" className="as-filter-tab" onClick={() => setViewingHistoryUserId(r.userId)}>History</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {view === 'campaigns' && (
        <div className="as-signup-list">
          <div className="as-activity-toolbar">
            <button className="as-filter-tab" onClick={() => { setCampaigns(null); fetchCampaigns(); }}>↻ Refresh</button>
          </div>
          {campaignsLoading && <div className="as-loading">Loading…</div>}
          {campaigns && campaigns.length === 0 && <div className="as-empty">No campaigns yet — create one under "New Campaign".</div>}
          {campaigns && campaigns.map(c => (
            <div key={c._id} className="as-signup-card as-email-campaign-card">
              <div className="as-signup-details" style={{ width: '100%' }}>
                <div className="as-signup-headline"><strong>{c.name}</strong></div>
                <div className="as-card-meta">
                  Subject: {c.subject} · {c.filters.profileCompleteness !== 'either' ? `${c.filters.profileCompleteness} profiles only · ` : ''}
                  {c.filters.newMemberDays ? `joined in last ${c.filters.newMemberDays}d · ` : ''}
                  {c.sentCount} sent so far
                </div>
                <div className="as-email-actions">
                  <button className="as-filter-tab" onClick={() => runPreview({ communityId: c.communityId, newMemberDays: c.filters.newMemberDays, profileCompleteness: c.filters.profileCompleteness }, c._id)}>
                    👀 Preview
                  </button>
                  <button className="as-btn as-btn--approve" onClick={() => handleSendCampaign(c)} disabled={sendingCampaignId === c._id}>
                    {sendingCampaignId === c._id ? 'Sending…' : '📤 Send Now'}
                  </button>
                  <button className="as-btn as-btn--reject" onClick={() => handleDeleteCampaign(c)}>Delete</button>
                </div>
                {preview && (
                  <div className="as-email-preview">
                    <strong>{preview.count}</strong> would receive this right now
                    {preview.excludedAsAlreadySent > 0 && ` (${preview.excludedAsAlreadySent} already emailed, excluded)`}
                  </div>
                )}
                {sendResult && sendResult.campaignId === c._id && (
                  <div className="as-action-msg">
                    {sendResult.dryRun ? '🧪 DRY RUN — ' : '✅ '}
                    Attempted {sendResult.attempted}: {sendResult.sent} sent, {sendResult.dry_run} dry-run, {sendResult.failed} failed.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'log' && (
        <div>
          <div className="as-activity-toolbar">
            <button className="as-filter-tab" onClick={() => { setLogs(null); fetchLogs(); }}>↻ Refresh</button>
          </div>
          {logsLoading && <div className="as-loading">Loading…</div>}
          {logs && logs.length === 0 && <div className="as-empty">No emails sent yet.</div>}
          {logs && logs.length > 0 && (
            <table className="as-ab-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Campaign</th>
                  <th>Recipient</th>
                  <th>Status</th>
                  <th>Sent By</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l._id}>
                    <td>{fmtDate(l.sentAt)}</td>
                    <td>{l.campaignName}</td>
                    <td>{l.recipientEmail}</td>
                    <td>{l.status === 'sent' ? '✅ Sent' : l.status === 'dry_run' ? '🧪 Dry run' : `❌ ${l.errorMessage || 'Failed'}`}</td>
                    <td>{l.sentBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
        </>
      )}

      {viewingHistoryUserId && (
        <EmailHistoryPanel userId={viewingHistoryUserId} onClose={() => setViewingHistoryUserId(null)} />
      )}
    </div>
  );
};

export default EmailManagerTab;
